import type { Dayjs } from 'dayjs';

import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import type {
  InspectorFilterGroupId,
  ReschedulingReason,
  ShuffleInspector,
  ShuffleInspectorGroup,
} from './types';
import type { ShuffleFilterDraft, ShuffleRescheduleFilters } from './build-reschedule-request-body';
import {
  createEmptyShuffleFilterDraft,
  toShuffleRescheduleFilters,
} from './build-reschedule-request-body';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { DateRange } from 'src/components/range-availability-calendar';
import type { RescheduleProgressDTO, RescheduleProgressStatus } from 'src/types/dto/reschedule-progress.dto';
import {
  isRescheduleProgressRunning,
  isRescheduleProgressTerminal,
} from 'src/types/dto/reschedule-progress.dto';
import axios, { endpoints } from 'src/utils/axios';

import { Step1 } from './steps/step1';
import { Step2Filters } from './steps/step2-filters';
import { StepResults } from './steps/step-results';
import { isAxiosError } from 'axios';

export { Step1 } from './steps/step1';
export { Step2Filters } from './steps/step2-filters';
export { StepResults } from './steps/step-results';
export {
  StepHeader,
  StepHeaderLabelPill,
  StepHeaderDropdownPill,
  StepHeaderDivider,
  SelectedDatesDropdownPill,
  SelectedInspectorsDropdownPill,
} from './step-header';
export type { StepHeaderDropdownOption } from './step-header';
export { getEarliestActivityAppointment } from './get-earliest-appointment';
export { getCurrentActivityInspector } from './get-current-activity-inspector';
export {
  getDefaultAvailabilityWindow,
  getDefaultPreferredRange,
} from './get-default-availability-ranges';
export { formatCreateAvailabilityLabelFromAppointment } from './format-create-availability-label';
export {
  formatSelectedDateRangeLabel,
  groupSelectedDatesIntoRanges,
} from './group-selected-dates';
export {
  InspectorAvailabilityGrid,
  InspectorAvailabilityGridSkeletonLoading,
  mapRescheduleSuggestionToResult,
} from './inspector-availability-grid';
export type { InspectorAvailabilityResult } from './inspector-availability-grid';
export type { SelectedDateRangeOption } from './group-selected-dates';
export type {
  ShuffleFilterDraft,
  ShuffleRescheduleFilters,
} from './build-reschedule-request-body';
export {
  buildRescheduleRequestBody,
  createEmptyShuffleFilterDraft,
  toShuffleRescheduleFilters,
} from './build-reschedule-request-body';
export type {
  InspectorFilterGroupId,
  ReschedulingReason,
  ShuffleInspector,
  ShuffleInspectorGroup,
} from './types';
export { INSPECTOR_FILTER_GROUP_IDS, RESCHEDULING_REASONS } from './types';
export { RescheduleConfirmationModal } from './reschedule-confirmation-modal';
export {
  buildRescheduleConfirmationSteps,
  type RescheduleConfirmationStep,
  type RescheduleConfirmationStepKey,
} from './build-reschedule-confirmation-steps';

interface ShuffleProps {
  activityId: number;
  currentActivity: ActivityDTO;
  projectId: number;
  competencyIds?: number[];
  appointment?: AppointmentDTO;
  startDate?: string | Date;
  dueDate?: string | Date;
  availabilityWindow?: DateRange;
  availabilityPreferred?: DateRange;
  availabilityExcluded?: DateRange;
  onMonthRangeChange?: (range: { from: Dayjs; to: Dayjs }) => void;
  availabilityLoading?: boolean;
  isDeskService?: boolean;
  onRescheduleConfirm?: () => void | Promise<void>;
  siteTimezoneId?: string;
  onDismiss?: () => void;
}

export const Shuffle = ({
  activityId,
  currentActivity,
  projectId,
  competencyIds: _competencyIds = [],
  appointment,
  startDate: _startDate,
  dueDate: _dueDate,
  availabilityWindow: _availabilityWindow,
  availabilityPreferred: _availabilityPreferred,
  availabilityExcluded: _availabilityExcluded,
  onMonthRangeChange: _onMonthRangeChange,
  availabilityLoading: _availabilityLoading = false,
  isDeskService = false,
  onRescheduleConfirm,
  siteTimezoneId,
  onDismiss,
}: ShuffleProps) => {
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState<ReschedulingReason | null>(null);
  const [filterDraft, setFilterDraft] = useState<ShuffleFilterDraft>(createEmptyShuffleFilterDraft);
  const [appliedFilters, setAppliedFilters] = useState<ShuffleRescheduleFilters | null>(null);
  const [selectedInspectors, setSelectedInspectors] = useState<ShuffleInspector[]>([]);
  const [rescheduleRunId, setRescheduleRunId] = useState(0);
  const [resumeProgressId, setResumeProgressId] = useState<number | null>(null);
  const [hasActiveProgress, setHasActiveProgress] = useState(false);
  const [activeProgressId, setActiveProgressId] = useState<number | null>(null);
  const [isProgressRunning, setIsProgressRunning] = useState(false);
  const [progressStatus, setProgressStatus] = useState<RescheduleProgressStatus | null>(null);
  const [progressSucceededCount, setProgressSucceededCount] = useState<number | undefined>(
    undefined
  );
  const [isCheckingActiveProgress, setIsCheckingActiveProgress] = useState(true);
  const [note, setNote] = useState('');
  const stopRescheduleRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadActiveProgress = async () => {
      setIsCheckingActiveProgress(true);

      try {
        const { data } = await axios.get<RescheduleProgressDTO>(
          endpoints.activities.latestRescheduleProgress(activityId),
          { params: { isActive: true } },
        );

        if (cancelled) {
          return;
        }

        setResumeProgressId(data.id);
        setHasActiveProgress(true);
        setProgressStatus(data.status);
        setStep(4);
        setIsProgressRunning(isRescheduleProgressRunning(data.status));
      } catch (error) {
        if (!cancelled && !(isAxiosError(error) && error.response?.status === 404)) {
          // Ignore missing active progress; surface unexpected errors in console only.
          console.error('Failed to load active reschedule progress', error);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingActiveProgress(false);
        }
      }
    };

    void loadActiveProgress();

    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const resetWizard = useCallback(async (progressIdToDeactivate?: number | null) => {
    const progressId =
      progressIdToDeactivate ?? resumeProgressId ?? activeProgressId;

    stopRescheduleRef.current?.();

    setStep(1);
    setSelectedReason(null);
    setFilterDraft(createEmptyShuffleFilterDraft());
    setAppliedFilters(null);
    setSelectedInspectors([]);
    setRescheduleRunId(0);
    setResumeProgressId(null);
    setHasActiveProgress(false);
    setActiveProgressId(null);
    setIsProgressRunning(false);
    setProgressStatus(null);
    setProgressSucceededCount(undefined);
    stopRescheduleRef.current = null;
    setNote('');

    if (progressId) {
      try {
        await axios.post(endpoints.activities.deactivateRescheduleProgress(progressId));
      } catch (error) {
        console.error('Failed to deactivate reschedule progress', error);
      }
    }
  }, [activeProgressId, resumeProgressId]);

  const handleFilterDraftChange = useCallback((patch: Partial<ShuffleFilterDraft>) => {
    setFilterDraft((current) => ({ ...current, ...patch }));
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilterDraft(createEmptyShuffleFilterDraft());
  }, []);

  const handleWizardReset = useCallback(async () => {
    await resetWizard(resumeProgressId ?? activeProgressId);
  }, [activeProgressId, resetWizard, resumeProgressId]);

  const handleStart = useCallback(
    (selectedInspectorsForRun: ShuffleInspector[]) => {
      if (isProgressRunning) {
        stopRescheduleRef.current?.();
        return;
      }

      setHasActiveProgress(false);
      setActiveProgressId(null);
      setResumeProgressId(null);
      setProgressStatus(null);
      setProgressSucceededCount(undefined);
      setAppliedFilters({
        ...toShuffleRescheduleFilters(filterDraft, {
          selectedReason,
          currentActivity,
          appointment,
        }),
        selectedReason,
        notes: note.trim(),
      });
      setSelectedInspectors(selectedInspectorsForRun);
      setRescheduleRunId((current) => current + 1);
      setHasActiveProgress(true);
      if (step === 2) {
        setStep(4);
      }
    },
    [appointment, currentActivity, filterDraft, isProgressRunning, note, selectedReason, step]
  );

  const registerStopReschedule = useCallback((stop: (() => void) | null) => {
    stopRescheduleRef.current = stop;
  }, []);

  const handleProgressStopped = useCallback(() => {
    setIsProgressRunning(false);
  }, []);

  const showStartOver =
    progressStatus !== null && isRescheduleProgressTerminal(progressStatus);

  const showFilters =
    !isCheckingActiveProgress &&
    (step === 2 || step === 4);
  const showResults = step === 4 && (appliedFilters || resumeProgressId);

  return (
    <Box sx={{ mb: 2 }}>
      {step === 1 && !isCheckingActiveProgress ? (
        <Step1
          selectedReason={selectedReason}
          note={note}
          onNoteChange={setNote}
          onReasonChange={setSelectedReason}
          onNext={() => {
            setStep(2);
          }}
        />
      ) : null}

      {showFilters ? (
        <Step2Filters
          activityId={activityId}
          projectId={projectId}
          currentActivity={currentActivity}
          selectedReason={selectedReason ?? 'other'}
          appointment={appointment}
          filters={filterDraft}
          onFiltersChange={handleFilterDraftChange}
          onReset={handleFilterReset}
          onWizardReset={handleWizardReset}
          showWizardReset={showStartOver}
          showFilterReset={step === 2 || hasActiveProgress}
          onStart={handleStart}
          isProgressRunning={isProgressRunning}
          optionsCount={progressSucceededCount}
        />
      ) : null}

      {showResults ? (
        <StepResults
          key={resumeProgressId ?? rescheduleRunId}
          activityId={activityId}
          runId={resumeProgressId ?? rescheduleRunId}
          currentActivity={currentActivity}
          filters={appliedFilters}
          resumeProgressId={resumeProgressId ?? undefined}
          isDeskService={isDeskService}
          onClose={() => {
            void resetWizard(resumeProgressId ?? activeProgressId);
          }}
          onActiveProgressIdChange={setActiveProgressId}
          onProgressStatusChange={setProgressStatus}
          onProgressSucceededChange={setProgressSucceededCount}
          onRescheduleConfirm={onRescheduleConfirm}
          onRunningChange={setIsProgressRunning}
          onProgressStopped={handleProgressStopped}
          registerStop={registerStopReschedule}
          siteTimezoneId={siteTimezoneId}
          onDismiss={onDismiss}
        />
      ) : null}
    </Box>
  );
};
